import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Users, User as UserIcon, X } from '../../icons';
import { Avatar } from '../../components/shared';
import {
  TASK_STATUSES, TASK_STATUS_META, TASK_PRIORITY_META,
  type TaskDto, type TaskStatus, type TaskPriority,
} from '../../api/tasks';
import { listTeamMembers, type TeamMemberDto } from '../../api/project-teams';
import { getUser } from '../../api/users';
import { useAuthStore } from '../../store/authStore';
import './TeamGantt.css';

/** 이름/아바타 해석된 사용자 (팀 멤버 또는 단건 조회 결과 공통 형태). */
interface ResolvedUser { name: string; avatarUrl: string | null; }

/**
 * TeamGantt — 업무관리(칸반) 상단 팀 미니 간트(진행률 채움 + 인라인 확장 행).
 *
 * 확정 시안(task-timeline-mockups): #2 진행률 채움 + #1 인라인 확장 행 + 전체/내 것만 필터.
 * 소스 회귀 금지(CLAUDE.md): 신규 파일 · additive. TaskPage 의 기존 tasks 를 재사용(추가 fetch 없음).
 *
 * - 행 = 팀원(태스크 담당자). 막대 길이 = 담당 건수 비율, 채움 = 완료율, 색 = 현재 집중 단계.
 * - 막대 클릭 → 그 행 아래로 단계 탭(할일/진행중/리뷰/완료) + 해당 단계 태스크 인라인 펼침.
 * - 필터: 전체 팀원 ↔ 내 것만(로그인 사용자).
 */

interface TeamGanttProps {
  tasks: TaskDto[];
  teamId: string | null;
  /**
   * 펼친 행의 태스크를 누르면 호출된다 — 팀 진행 현황에서 바로 상세로 가기 위한 통로.
   * 미전달이면 항목은 종전처럼 **눌리지 않는 표시 전용**이라 기존 호출부 동작이 그대로다.
   */
  onOpenTask?: (task: TaskDto) => void;
}

interface MemberRow {
  userId: string;
  counts: Record<TaskStatus, number>;
  total: number;
  done: number;
  byStatus: Record<TaskStatus, TaskDto[]>;
  // 시간축(시안의 막대 시작 오프셋) — 멤버 태스크의 createdAt~dueDate 범위. 유효 날짜 없으면 Infinity/-Infinity.
  startMs: number;
  endMs: number;
}

/** ISO 문자열 → epoch ms (없거나 파싱 불가 시 NaN). */
function ms(s: string | null | undefined): number {
  if (!s) return NaN;
  const v = Date.parse(s);
  return Number.isNaN(v) ? NaN : v;
}

// 막대 색 우선순위 — 멤버가 "지금 무엇을 하고 있나"(진행 중 > 리뷰 > 할 일 > 완료).
const FOCUS_ORDER: TaskStatus[] = ['IN_PROGRESS', 'REVIEW', 'TODO', 'DONE'];
// 담당자 미지정 태스크 집계 행(신규 태스크는 기본 무담당 → 추가 즉시 여기 반영).
const UNASSIGNED = '__unassigned__';
// 팀원별 막대 색 팔레트 — 상태색 단색 대신 멤버를 색으로 구분(가독성↑).
const BAR_PALETTE = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#6366f1', '#f97316', '#14b8a6', '#ef4444'];
const UNASSIGNED_COLOR = '#94a3b8';

function emptyCounts(): Record<TaskStatus, number> {
  return { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 };
}
function emptyByStatus(): Record<TaskStatus, TaskDto[]> {
  return { TODO: [], IN_PROGRESS: [], REVIEW: [], DONE: [] };
}
function normStatus(s: string): TaskStatus {
  return (TASK_STATUSES as string[]).includes(s) ? (s as TaskStatus) : 'TODO';
}
function focusStage(row: MemberRow): TaskStatus {
  return FOCUS_ORDER.find((s) => row.counts[s] > 0) ?? 'TODO';
}

export function TeamGantt({ tasks, teamId, onOpenTask }: TeamGanttProps) {
  const authUser = useAuthStore((s) => s.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';

  const [members, setMembers] = useState<Record<string, TeamMemberDto>>({});
  // 팀 외(개인 프로젝트 등) 담당자 이름/아바타 단건 해석 캐시.
  const [resolved, setResolved] = useState<Record<string, ResolvedUser>>({});
  const [filter, setFilter] = useState<'all' | 'mine'>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [stage, setStage] = useState<TaskStatus>('IN_PROGRESS');

  // 팀 소속 프로젝트면 멤버 이름/아바타 해석 (개인 프로젝트는 생략).
  useEffect(() => {
    let alive = true;
    if (!teamId) { setMembers({}); return; }
    void listTeamMembers(teamId)
      .then((list) => {
        if (!alive) return;
        const map: Record<string, TeamMemberDto> = {};
        for (const m of list) map[m.userId] = m;
        setMembers(map);
      })
      .catch(() => { if (alive) setMembers({}); });
    return () => { alive = false; };
  }, [teamId]);

  // 팀 멤버로 해석되지 않은 담당자 id 를 단건 조회로 보강 (개인 프로젝트 포함). 본인은 authStore 로 해석.
  useEffect(() => {
    let alive = true;
    const ids = new Set<string>();
    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      for (const uid of (Array.isArray(t.assignees) ? t.assignees : [])) ids.add(uid);
    }
    const missing = [...ids].filter(
      (uid) => uid && uid !== myId && !members[uid] && !resolved[uid],
    );
    if (missing.length === 0) return;
    void Promise.all(
      missing.map((uid) =>
        getUser(uid)
          .then((u) => [uid, { name: u.name, avatarUrl: u.avatarUrl }] as const)
          .catch(() => null),
      ),
    ).then((pairs) => {
      if (!alive) return;
      const add: Record<string, ResolvedUser> = {};
      for (const p of pairs) if (p) add[p[0]] = p[1];
      if (Object.keys(add).length) setResolved((prev) => ({ ...prev, ...add }));
    });
    return () => { alive = false; };
  }, [tasks, members, resolved, myId]);

  // 담당자별 집계 (담당자 미지정 태스크는 '미지정' 행으로 — 신규 태스크 추가 즉시 반영).
  const rows = useMemo<MemberRow[]>(() => {
    const map = new Map<string, MemberRow>();
    for (const t of (Array.isArray(tasks) ? tasks : [])) {
      const st = normStatus(t.status);
      const al = Array.isArray(t.assignees) ? t.assignees : [];
      const ids = al.length ? al : [UNASSIGNED];
      const c = ms(t.createdAt);
      const d = ms(t.dueDate);
      for (const uid of ids) {
        let row = map.get(uid);
        if (!row) {
          row = { userId: uid, counts: emptyCounts(), total: 0, done: 0, byStatus: emptyByStatus(), startMs: Infinity, endMs: -Infinity };
          map.set(uid, row);
        }
        row.counts[st] += 1;
        row.total += 1;
        if (st === 'DONE') row.done += 1;
        row.byStatus[st].push(t);
        if (!Number.isNaN(c)) { row.startMs = Math.min(row.startMs, c); row.endMs = Math.max(row.endMs, c); }
        if (!Number.isNaN(d)) { row.endMs = Math.max(row.endMs, d); if (Number.isNaN(c)) row.startMs = Math.min(row.startMs, d); }
      }
    }
    const arr = [...map.values()];
    // 로그인 사용자 우선 → 미지정은 맨 뒤 → 그 외 담당 건수 많은 순.
    arr.sort((a, b) => {
      if (a.userId === myId) return -1;
      if (b.userId === myId) return 1;
      if (a.userId === UNASSIGNED) return 1;
      if (b.userId === UNASSIGNED) return -1;
      return b.total - a.total;
    });
    return arr;
  }, [tasks, myId]);

  const visibleRows = filter === 'mine' ? rows.filter((r) => r.userId === myId) : rows;
  const maxTotal = Math.max(1, ...rows.map((r) => r.total));
  const totalTasks = rows.reduce((a, r) => a + r.total, 0);
  const memberCount = rows.filter((r) => r.userId !== UNASSIGNED).length;

  // 팀원별 막대 색 매핑(미지정=회색). rows 순서 기반이라 필터와 무관하게 색 고정.
  const colorByUser = useMemo(() => {
    const m: Record<string, string> = {};
    let i = 0;
    for (const r of rows) {
      if (r.userId === UNASSIGNED) { m[r.userId] = UNASSIGNED_COLOR; continue; }
      m[r.userId] = BAR_PALETTE[i % BAR_PALETTE.length]!;
      i += 1;
    }
    return m;
  }, [rows]);

  // 단계별 태스크 건수(팀 전체, 태스크 1건=1카운트 — 담당자 중복 없이).
  const statusCounts = useMemo(() => {
    const c: Record<TaskStatus, number> = { TODO: 0, IN_PROGRESS: 0, REVIEW: 0, DONE: 0 };
    for (const t of (Array.isArray(tasks) ? tasks : [])) c[normStatus(t.status)] += 1;
    return c;
  }, [tasks]);

  // 시안형 시간축 — 전체 태스크 날짜 범위. 유효 날짜가 충분하면 막대 시작/길이를 날짜로 배치.
  let winMin = Infinity;
  let winMax = -Infinity;
  for (const r of rows) {
    if (r.startMs !== Infinity) winMin = Math.min(winMin, r.startMs);
    if (r.endMs !== -Infinity) winMax = Math.max(winMax, r.endMs);
  }
  const hasTimeline = winMax > winMin;
  const todayPct = hasTimeline ? ((Date.now() - winMin) / (winMax - winMin)) * 100 : -1;

  /**
   * 행 막대의 left/width(%).
   * - width = 담당 건수(업무량) 비례 — 마감일 희소로 인한 기간 기반 너비 폭증 방지(안정적 크기).
   * - left  = 시작일(createdAt) 기반 오프셋(시안의 다른 시작점) — 날짜축이 있을 때만, 막대가 항상 다 보이도록 clamp.
   */
  function barGeom(row: MemberRow): { left: number; width: number } {
    const width = Math.max(14, Math.round((row.total / maxTotal) * 90));
    let left = 0;
    if (hasTimeline && row.startMs !== Infinity) {
      const dateLeft = ((row.startMs - winMin) / (winMax - winMin)) * 100;
      left = Math.min(Math.max(0, dateLeft), 100 - width);
    }
    return { left, width };
  }

  function memberName(userId: string): string {
    if (userId === UNASSIGNED) return '미지정';
    if (userId === myId) return authUser?.name ?? members[userId]?.name ?? resolved[userId]?.name ?? '나';
    return members[userId]?.name ?? resolved[userId]?.name ?? `사용자 ${userId}`;
  }
  function memberAvatar(userId: string): string | null {
    return members[userId]?.avatarUrl ?? resolved[userId]?.avatarUrl ?? null;
  }

  function selectRow(row: MemberRow) {
    if (selected === row.userId) { setSelected(null); return; }
    setSelected(row.userId);
    setStage(focusStage(row));
  }
  function changeFilter(next: 'all' | 'mine') {
    setFilter(next);
    if (next === 'mine') {
      const me = rows.find((r) => r.userId === myId);
      setSelected(me ? myId : null);
      if (me) setStage(focusStage(me));
    } else {
      setSelected(null);
    }
  }

  return (
    <section className="team-gantt" aria-label="팀 진행 현황">
      <div className="tg-head">
        <span className="tg-title">
          팀 진행 현황
          <small> · 팀원 {memberCount}명 · 전체 {totalTasks}건</small>
        </span>
        <span className="tg-stats" aria-label="단계별 건수">
          {TASK_STATUSES.map((s, i) => (
            <span
              key={s}
              className="tg-stat"
              style={{ ['--c' as string]: TASK_STATUS_META[s].color, animationDelay: `${i * 70}ms` } as CSSProperties}
            >
              <i className="tg-stat-dot" />
              <span className="tg-stat-label">{TASK_STATUS_META[s].label}</span>
              <b className="tg-stat-num" key={statusCounts[s]}>{statusCounts[s]}</b>
            </span>
          ))}
        </span>
        <span className="tg-filter" role="group" aria-label="보기 범위">
          <button
            type="button"
            className={`tg-fbtn ${filter === 'all' ? 'tg-fbtn--on' : ''}`}
            onClick={() => changeFilter('all')}
            aria-pressed={filter === 'all'}
            title="전체 팀원"
          >
            <Users size={13} />전체
          </button>
          <button
            type="button"
            className={`tg-fbtn ${filter === 'mine' ? 'tg-fbtn--on' : ''}`}
            onClick={() => changeFilter('mine')}
            aria-pressed={filter === 'mine'}
            title="내 것만"
          >
            <UserIcon size={13} />내 것만
          </button>
        </span>
      </div>

      <div className="tg-body">
        {visibleRows.length === 0 && (
          <div className="tg-empty">{filter === 'mine' ? '내 담당 태스크가 없습니다.' : '담당자가 지정된 태스크가 없습니다.'}</div>
        )}

        {visibleRows.map((row) => {
          const isSel = selected === row.userId;
          const fs = focusStage(row);
          const barColor = colorByUser[row.userId] ?? UNASSIGNED_COLOR; // 팀원별 색
          const { left: leftPct, width: widthPct } = barGeom(row);
          const progressPct = row.total ? Math.round((row.done / row.total) * 100) : 0;
          // 시안: 막대 안에 대표 작업 제목 + 우측 완료수 (흰색 진행 오버레이가 완료율 표시).
          const barTitle = row.byStatus[fs][0]?.title ?? memberName(row.userId);
          return (
            <div className="tg-rowgroup" key={row.userId}>
              <div className="tg-row">
                <span className="tg-who">
                  <Avatar userId={Number(row.userId) || 0} name={memberName(row.userId)} src={memberAvatar(row.userId)} size="xs" />
                  <span className="tg-name">{memberName(row.userId)}{row.userId === myId && <em className="tg-me"> · 나</em>}</span>
                </span>
                <span className="tg-lane">
                  {todayPct >= 0 && todayPct <= 100 && (
                    <span className="tg-today" style={{ left: `${todayPct}%` }} aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    className={`tg-bar ${isSel ? 'tg-bar--sel' : ''}`}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%`, background: barColor }}
                    onClick={() => selectRow(row)}
                    aria-expanded={isSel}
                    aria-label={`${memberName(row.userId)} · ${row.total}건 중 완료 ${row.done}건`}
                    title={`${memberName(row.userId)} — 완료 ${row.done}/${row.total}`}
                  >
                    <i className="tg-fill" style={{ width: `${progressPct}%` }} />
                    <em className="tg-bar-label">{barTitle}</em>
                    <em className="tg-bar-count">{row.done}/{row.total}</em>
                  </button>
                </span>
              </div>

              {isSel && (
                <div className="tg-detail">
                  <div className="tg-tabs" role="tablist">
                    {TASK_STATUSES.map((s) => {
                      const c = row.counts[s];
                      const on = s === stage;
                      return (
                        <button
                          key={s}
                          type="button"
                          role="tab"
                          aria-selected={on}
                          className={`tg-tab ${on ? 'tg-tab--on' : ''}`}
                          style={on ? { background: TASK_STATUS_META[s].color, borderColor: 'transparent', color: '#fff' } : undefined}
                          disabled={c === 0}
                          onClick={() => setStage(s)}
                        >
                          {TASK_STATUS_META[s].label} {c}
                        </button>
                      );
                    })}
                    <button type="button" className="tg-close" onClick={() => setSelected(null)} aria-label="닫기" title="닫기">
                      <X size={13} />
                    </button>
                  </div>
                  <div className="tg-list">
                    {row.byStatus[stage].length === 0 && (
                      <div className="tg-list-empty">이 단계에 작업이 없습니다.</div>
                    )}
                    {row.byStatus[stage].map((t) => {
                      const pm = TASK_PRIORITY_META[t.priority as TaskPriority] ?? null;
                      return (
                        <div
                          className={`tg-item${onOpenTask ? ' tg-item--click' : ''}`}
                          key={t.id}
                          {...(onOpenTask
                            ? {
                                role: 'button',
                                tabIndex: 0,
                                title: t.title,
                                onClick: () => onOpenTask(t),
                                onKeyDown: (e: React.KeyboardEvent) => {
                                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenTask(t); }
                                },
                              }
                            : {})}
                        >
                          <span className="tg-item-dot" style={{ background: TASK_STATUS_META[stage].color }} />
                          {pm && <span className="tg-item-pri" style={{ background: pm.color }}>{pm.label}</span>}
                          <span className="tg-item-title">{t.title}</span>
                          {t.dueDate
                            ? <span className="tg-item-due">{t.dueDate}</span>
                            : <span className="tg-item-nodue">기한 없음</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
